import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, MousePointerClick } from "lucide-react";
import { SITE } from "../config/site";
import { SERVICES } from "../data/services";
import { KineticText } from "../components/KineticText";
import { ParticleField } from "../components/ParticleField";
import { Reveal } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";

const MARQUEE_ITEMS = ["React", "Next.js", "Node.js", "Flutter", "Solidity", "Web3", "Python", "AWS", "Docker", "Figma", "TypeScript", "PostgreSQL"];

const MANIFESTO = [
  { num: "01", title: "Architectural Precision", text: "Every product starts as a blueprint. We design systems — not screens — so what we ship today never blocks what you need tomorrow." },
  { num: "02", title: "Kinetic User Experience", text: "Motion is meaning. We choreograph every transition and micro-interaction so your product feels alive, premium and unmistakably yours." },
  { num: "03", title: "Zero-Friction Engineering", text: "Weekly demos, transparent pricing, code you own. No black boxes, no vendor lock-in — just momentum from kickoff to launch." },
];

const STATS = [
  { to: 2, suffix: "+", label: "Years of Experience" },
  { to: 100, suffix: "+", label: "Happy Clients" },
  { to: 120, suffix: "+", label: "Projects Shipped" },
  { to: 15, suffix: "+", label: "Industries Served" },
];

const PROCESS = [
  { num: "01", title: "Discovery", text: "A deep-dive workshop to map goals, users and success metrics." },
  { num: "02", title: "Design", text: "Wireframes to pixel-perfect prototypes, reviewed with you weekly." },
  { num: "03", title: "Development", text: "Sprint-based engineering with staging previews from week one." },
  { num: "04", title: "Launch & Scale", text: "Deployment, monitoring and iteration — we stay after go-live." },
];

const TESTIMONIALS = [
  { quote: "SparkGrid rebuilt our entire platform in nine weeks. Page loads dropped by 60% and demo bookings doubled within a month.", name: "Rohan Deshmukh", role: "Founder, FinEdge (Fintech)" },
  { quote: "They think like product partners, not vendors. The crypto dashboard they built handled our token launch traffic flawlessly.", name: "Priya Nair", role: "CPO, ChainVault (Web3)" },
  { quote: "From the first call to launch day, everything was transparent. Our booking app now holds a 4.8-star rating on both stores.", name: "Amit Shah", role: "Director, UrbanNest (Real Estate)" },
];

const spanFor = (i) => (i < 2 ? "lg:col-span-3" : "lg:col-span-2");

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="grain relative flex min-h-screen items-center overflow-hidden bg-night pt-24" data-testid="hero-section">
        <ParticleField className="absolute inset-0 h-full w-full" />
        <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-spark/15 blur-[120px]" />
        <div className="pointer-events-none absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-ember/15 blur-[120px]" />

        <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-4 pb-20 sm:px-6 lg:grid-cols-12 lg:px-8">
          <motion.div style={{ y: heroY, opacity: heroOpacity }} className="text-center lg:col-span-8 lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mb-8 inline-flex max-w-full items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur"
            >
              <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-ember" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 sm:text-xs sm:tracking-[0.25em]">
                Mumbai → Worldwide · Software Studio
              </span>
            </motion.div>

            <h1 className="font-syne text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              <KineticText
                lines={[
                  "INNOVATING",
                  <span key="b" className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">BEYOND</span>,
                  "LIMITS.",
                ]}
                delay={0.25}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg lg:mx-0"
            >
              {SITE.name} designs and engineers websites, web apps, mobile apps and crypto
              products for ambitious brands — from Mumbai to the world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-start"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-spark to-ember px-8 py-4 text-sm font-bold text-night transition-transform duration-300 hover:scale-105 sm:w-auto"
              >
                Start a Project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href="#services"
                data-testid="hero-cta-explore"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-spark hover:text-spark sm:w-auto"
              >
                Explore Services <ArrowDown className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: logoY }} className="hidden lg:col-span-4 lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-4/5"
            >
              <motion.img
                src={SITE.logo}
                alt="SparkGrid Solutions — particle swirl logo"
                data-testid="hero-logo"
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="w-full [mask-image:radial-gradient(circle,black_52%,transparent_70%)]"
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <MousePointerClick className="h-5 w-5 animate-bounce text-slate-500" />
        </motion.div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* SERVICES BENTO */}
      <section id="services" className="bg-silk py-20 lg:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">What We Do</p>
              <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
                Five crafts.
                <br />
                One standard: <span className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">excellence.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
                Every engagement is led by senior engineers and designers — no hand-offs, no juniors learning on your budget.
              </p>
            </Reveal>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.07} className={`md:col-span-1 ${spanFor(i)}`}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-ink/10"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-night/85 px-3 py-1 font-mono text-xs font-semibold text-spark backdrop-blur">
                      {s.num}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-6">
                    <h3 className="font-syne text-xl font-bold text-ink">{s.name}</h3>
                    <p className="text-sm leading-relaxed text-slate-500">{s.excerpt}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-ink">
                      Explore
                      <ArrowUpRight className="h-4 w-4 text-ember transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-ink/10 bg-white py-20 lg:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">The SparkGrid Manifesto</p>
          </Reveal>
          <div className="mt-10">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.08}>
                <div className="group grid gap-4 border-t border-ink/10 py-10 transition-colors duration-500 hover:bg-silk sm:grid-cols-12 sm:items-center sm:gap-8 lg:py-14">
                  <span className="text-outline-ink font-syne text-6xl font-extrabold sm:col-span-3 lg:text-8xl">
                    {m.num}
                  </span>
                  <h3 className="font-syne text-2xl font-bold text-ink sm:col-span-4 lg:text-3xl">{m.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500 sm:col-span-5 sm:text-base">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="grain relative overflow-hidden bg-night py-20 lg:py-28" data-testid="stats-section">
        <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[40rem] -translate-x-1/2 rounded-full bg-spark/10 blur-[120px]" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center lg:text-left">
              <p className="font-syne text-5xl font-extrabold text-white lg:text-6xl" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <CountUp to={s.to} suffix={s.suffix} />
              </p>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-silk py-20 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">How We Work</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
              Idea to launch, in four moves.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="group relative h-full rounded-3xl border border-ink/10 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:border-spark/40 hover:shadow-xl hover:shadow-spark/10">
                  <span className="font-mono text-sm font-semibold text-ember">{p.num}</span>
                  <h3 className="mt-4 font-syne text-xl font-bold text-ink">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{p.text}</p>
                  {i < PROCESS.length - 1 && (
                    <span className="absolute -right-4 top-1/2 hidden h-px w-8 bg-ink/15 lg:block" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-ink/10 bg-white py-20 lg:py-32" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Client Voices</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
              Trusted by founders who ship.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure className="flex h-full flex-col rounded-3xl border border-ink/10 bg-silk p-8 transition-transform duration-500 hover:-translate-y-1.5" data-testid={`testimonial-${i}`}>
                  <span className="font-syne text-5xl leading-none text-ember">&ldquo;</span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-600 sm:text-base">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 border-t border-ink/10 pt-4">
                    <p className="font-syne text-sm font-bold text-ink">{t.name}</p>
                    <p className="mt-0.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
